import _ from "lodash";
import React, { useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { useStaticPlot } from "../../../shared/providers/StaticPlotProvider";
import { MapWithPathSupport } from "../../../shared/utilities";
import { RootState } from "../../../store";

type CanvasEventHandlerProps = {
    enableFunctionCaching: boolean;
    includeInitialChanges: boolean;

    onEnter: () => void;
    onLeave: () => void;
    onParameterValuesChanged: () => void;
    onParameterValueChanged: (parameterName: string, newValue: any, oldValue: any) => void;
    onFrameDataChanged: () => void;
    onFieldValueChanged: (fieldName: string, newValue: any, oldValue: any) => void;
    onDatasourceRefreshStart: (datasourceName: string) => void;
    onDatasourceRefreshEnd: (datasourceName: string) => void;
    onDatasourceUpdated: (datasourceName: string, newValue: any, oldValue: any) => void;
    onDatasourceError: (datasourceName: string, error: any) => void;
};

const CANVAS_EVENT_HANDLER: React.FC<CanvasEventHandlerProps> = (props) => {
    const {
        onEnter,
        onLeave,
        onParameterValuesChanged,
        onParameterValueChanged,
        onFrameDataChanged,
        onFieldValueChanged,
        onDatasourceRefreshStart,
        onDatasourceRefreshEnd,
        onDatasourceUpdated,
        onDatasourceError,
        includeInitialChanges = false,
    } = props;

    const parameterValues = useSelector((s: RootState) => s.storyline.parameterValues);
    const currentFrame = useSelector((s: RootState) => s.storyline.currentParagraph.data);
    const datasourcesInFlight = useSelector((s: RootState) => s.storyline.datasourcesInFlight);
    const datasourceValues = useSelector((s: RootState) => s.storyline.datasourceValues);
    const datasourceErrors = useSelector((s: RootState) => s.storyline.datasourceErrors);

    // Optimization to avoid unnecessary cloning of the initial values on each render...
    const _lastParameterValues = useRef<MapWithPathSupport<any>>(null);
    if (_lastParameterValues.current === null && (onParameterValuesChanged || onParameterValueChanged)) {
        _lastParameterValues.current = includeInitialChanges ? new MapWithPathSupport() : _.cloneDeep(parameterValues ?? new MapWithPathSupport());
    }
    const _lastFrameData = useRef<Record<string, any>>(null);
    if (_lastFrameData.current === null && (onFrameDataChanged || onFieldValueChanged)) {
        _lastFrameData.current = includeInitialChanges ? {} : _.cloneDeep(currentFrame);
    }
    const _lastDatasourceValues = useRef<Map<string, any>>(null);
    if (_lastDatasourceValues.current === null && onDatasourceUpdated) {
        _lastDatasourceValues.current = includeInitialChanges ? new Map() : _.cloneDeep(datasourceValues);
    }
    const _lastDatasourceErrors = useRef<Map<string, any>>(null);
    if (_lastDatasourceErrors.current === null && onDatasourceError) {
        _lastDatasourceErrors.current = includeInitialChanges ? new Map() : _.cloneDeep(datasourceErrors);
    }
    const _lastDatasourcesInFlight = useRef<string[]>(null);
    if (_lastDatasourcesInFlight.current === null && (onDatasourceRefreshStart || onDatasourceRefreshEnd)) {
        _lastDatasourcesInFlight.current = [];
    }

    const lastParameterValues = _lastParameterValues.current;
    const lastFrameData = _lastFrameData.current;
    const lastDatasourceValues = _lastDatasourceValues.current;
    const lastDatasourceErrors = _lastDatasourceErrors.current;
    const lastDatasourcesInFlight = _lastDatasourcesInFlight.current;

    useEffect(() => {
        onEnter && onEnter();
        return onLeave;
    }, []);

    useEffect(() => {
        if (!parameterValues) return;
        if (!onParameterValuesChanged && !onParameterValueChanged) return;

        if (!_.isEqual(lastParameterValues, parameterValues)) {
            onParameterValuesChanged && onParameterValuesChanged();

            for (let key of new Set([...lastParameterValues.keys(), ...parameterValues.keys()])) {
                const newValue = parameterValues.get(key);

                if (!lastParameterValues.has(key)) {
                    onParameterValueChanged?.(key, newValue, null);
                } else {
                    const oldValue = lastParameterValues.get(key);

                    if (!_.isEqual(oldValue, newValue)) {
                        onParameterValueChanged?.(key, newValue, oldValue);
                    }
                }

                // Mutate the last parameter values to reflect the current state...
                if (parameterValues.has(key)) {
                    lastParameterValues.set(key, newValue);
                } else {
                    lastParameterValues.delete(key);
                }
            }
        }
    }, [parameterValues]);

    useEffect(() => {
        if (!currentFrame) return;
        if (!onFrameDataChanged && !onFieldValueChanged) return;

        if (!_.isEqual(lastFrameData, currentFrame)) {
            onFrameDataChanged && onFrameDataChanged();

            for (let key of new Set([...Object.keys(lastFrameData), ...Object.keys(currentFrame)])) {
                const newValue = currentFrame[key];

                if (!Object.hasOwn(lastFrameData, key)) {
                    onFieldValueChanged?.(key, newValue, null);
                } else {
                    const oldValue = lastFrameData[key];

                    if (!_.isEqual(oldValue, newValue)) {
                        onFieldValueChanged?.(key, newValue, oldValue);
                    }
                }

                // Mutate the last frame data to reflect the current state...
                if (Object.hasOwn(currentFrame, key)) {
                    lastFrameData[key] = currentFrame[key];
                }
                else {
                    delete lastFrameData[key];
                }
            }
        }
    }, [currentFrame]);

    useEffect(() => {
        if (!datasourcesInFlight) return;
        if (!onDatasourceRefreshStart && !onDatasourceRefreshEnd) return;

        const datasourcesInFlightArray = Array.from(datasourcesInFlight.values());

        const requestsAdded = _.difference(datasourcesInFlightArray, lastDatasourcesInFlight);
        const requestsRemoved = _.difference(lastDatasourcesInFlight, datasourcesInFlightArray);

        requestsAdded.forEach(r => {
            onDatasourceRefreshStart?.(r);
            lastDatasourcesInFlight.push(r);
        });
        requestsRemoved.forEach(r => {
            onDatasourceRefreshEnd?.(r);
            _lastDatasourcesInFlight.current = lastDatasourcesInFlight.filter(x => x !== r);
        });
    }, [datasourcesInFlight]);

    useEffect(() => {
        if (!datasourceValues) return;
        if (!onDatasourceUpdated) return;

        if (!_.isEqual(lastDatasourceValues, datasourceValues)) {
            for (let key of new Set([...lastDatasourceValues.keys(), ...datasourceValues.keys()])) {
                const newValue = datasourceValues.get(key);

                if (!lastDatasourceValues.has(key)) {
                    onDatasourceUpdated?.(key, newValue, null);
                } else {
                    const oldValue = lastDatasourceValues.get(key);

                    if (!_.isEqual(oldValue, newValue)) {
                        onDatasourceUpdated?.(key, newValue, oldValue);
                    }
                }

                // Mutate the last values to reflect the current state...
                if (datasourceValues.has(key)) {
                    lastDatasourceValues.set(key, newValue);
                } else {
                    lastDatasourceValues.delete(key);
                }
            }
        }
    }, [datasourceValues]);

    useEffect(() => {
        if (!datasourceErrors) return;
        if (!onDatasourceError) return;

        if (!_.isEqual(lastDatasourceErrors, datasourceErrors)) {
            for (let key of new Set([...lastDatasourceErrors.keys(), ...datasourceErrors.keys()])) {
                const newValue = datasourceErrors.get(key);

                if (!lastDatasourceErrors.has(key)) {
                    onDatasourceError?.(key, newValue);
                } else {
                    const oldValue = lastDatasourceErrors.get(key);

                    if (!_.isEqual(oldValue, newValue)) {
                        onDatasourceError?.(key, newValue);
                    }
                }

                // Mutate the last values to reflect the current state...
                if (datasourceErrors.has(key)) {
                    lastDatasourceErrors.set(key, newValue);
                } else {
                    lastDatasourceErrors.delete(key);
                }
            }
        }
    }, [datasourceErrors]);

    return null;
}

const CanvasEventHandler: React.FC<CanvasEventHandlerProps> = (props) => {
    const staticPlot = useStaticPlot();
    if (staticPlot) return;

    return <CANVAS_EVENT_HANDLER {...props} />;
}

(CanvasEventHandler as DocumentedComponent).metadata = {
    description: `The \`CanvasEventHandler\` component can be used to add custom logic to the lifecycle events of a canvas.  Arbitrary Javascript code can be executed inside these event handlers in order to add novel behavior to the canvas.  Some examples of possible custom behavior:

* Redirecting to specific storylines based on User Metadata
* Populating parameter values based on User Metadata
* Starting/stopping animations
* Starting/stopping narration`,
    isSelfClosing: true,
    attributes: [
        { name: "includeInitialChanges", type: "boolean", description: "If `true`, the change from `null` to the initial value will also be included in all the relevant callbacks (data source updated, frame data field changes, parameter value changes, etc.).  This can be useful for components which need to implement side-effects for **all** value changes, such as manual data source refreshing or parameter value syncing logic. Optional, defaults to `false`." },
        { name: "onEnter", type: "function", exactType: "() => void", template: `onEnter={() => {\n\t$1\n}}`, description: `Callback which is executed the first time this \`CanvasEventHandler\` instance is rendered.  This is a good place to set up any initial state for the canvas, such as setting default parameter values or initializing the frame data.` },
        { name: "onLeave", type: "function", exactType: "() => void", template: `onLeave={() => {\n\t$1\n}}`, description: `Callback which is executed when this \`CanvasEventHandler\` instance is removed from the DOM.  This is a good place to clean up any state that was set up in the \`onEnter\` callback.` },
        { name: "onParameterValueChanged", type: "function", exactType: "(parameterName: string, newValue: any, oldValue: any) => void", template: `onParameterValueChanged={(parameterName, newValue, oldValue) => {\n\t$1\n}}`, description: `Callback which is executed when a parameter value has changed.  The \`parameterName\` input parameter can be used to determine which parameter was changed, whereas the \`newValue\` parameter will contain the updated value and \`oldValue\` will contain the previous value.` },
        { name: "onParameterValuesChanged", type: "function", exactType: "() => void", template: `onParameterValuesChanged={() => {\n\t$1\n}}`, description: `Callback which is executed when the parameter values have been updated.  The function can use \`this.parameterValues\` to access the parameter values map.` },
        { name: "onFrameDataChanged", type: "function", exactType: "() => void", template: `onFrameDataChanged={() => {\n\t$1\n}}`, description: `Callback which is executed when the frame data for the canvas has changed.` },
        { name: "onFieldValueChanged", type: "function", exactType: "(fieldName: string, newValue: any, oldValue: any) => void", template: `onFieldValueChanged={(fieldName, newValue, oldValue) => {\n\t$1\n}}`, description: `Callback which is executed when a field in the frame data has changed.  The \`fieldName\` input parameter can be used to determine which field has changed, whereas the \`newValue\` parameter will contain the updated value and \`oldValue\` will contain the previous value.` },
        { name: "onDatasourceRefreshStart", type: "function", exactType: "(datasourceName: string) => void", template: `onDatasourceRefreshStart={(datasourceName) => {\n\t$1\n}}`, description: `Callback which is executed when a datasource is being refreshed.  The network request should be in-flight at this stage.  The \`datasourcesInFlight\` field (available to the template bindings) should be preferred above manual parameter value management via this callback and its response counterpart.` },
        { name: "onDatasourceRefreshEnd", type: "function", exactType: "(datasourceName: string) => void", template: `onDatasourceRefreshEnd={(datasourceName) => {\n\t$1\n}}`, description: `Callback which is executed when a datasource refresh response has been received.  Useful in cases where dependent datasources need to be refreshed after some persistence action has occurred.  A timestamp or version response from the datasource can also be used to track this, but this callback may be cleaner/simpler in some cases.` },
        { name: "onDatasourceUpdated", type: "function", exactType: "(datasourceName: string, newValue: any, oldValue: any) => void", template: `onDatasourceUpdated={(datasourceName, newValue, oldValue) => {\n\t$1\n}}`, description: `Callback which is executed when a datasource value has changed.  The \`datasourceName\` input parameter can be used to determine which datasource was changed, whereas the \`newValue\` parameter will contain the updated value and \`oldValue\` will contain the previous value.` },
        { name: "onDatasourceError", type: "function", exactType: "(datasourceName: string, error: any) => void", template: `onDatasourceError={(datasourceName, error) => {\n\t$1\n}}`, description: `Callback which is executed when a datasource error has occurred.  The \`datasourceName\` input parameter can be used to determine which datasource was changed, whereas the \`error\` parameter will contain the error object.` },
    ]
};

export default CanvasEventHandler;